import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  CircleNotch,
  PaperPlaneRight,
  PauseCircle,
  Robot,
  ShieldSlash,
  WarningCircle
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const ErrorState = ({ icon: Icon, title, description }) => (
  <div className="min-h-screen bg-[#161B26] flex items-center justify-center px-6">
    <div className="text-center max-w-md">
      <Icon size={48} weight="duotone" className="text-[#555] mx-auto mb-4" />
      <h1 className="text-xl font-bold text-white mb-2">{title}</h1>
      <p className="text-sm text-[#8b8fa3]">{description}</p>
    </div>
  </div>
);

export default function PublicAgentPage() {
  const { publicKey } = useParams();
  const [agentInfo, setAgentInfo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    resolveAgent();
  }, [publicKey]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const resolveAgent = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/agent/${publicKey}`);
      if (res.status === 404) { setError('not_found'); return; }
      if (res.status === 410) { setError('revoked'); return; }
      if (res.status === 403) {
        const data = await res.json();
        if (data.detail?.includes('paused')) { setError('paused'); return; }
        if (data.detail?.includes('domain')) { setError('domain_blocked'); return; }
        setError('forbidden'); return;
      }
      if (!res.ok) throw new Error('Failed to resolve');
      const data = await res.json();
      setAgentInfo(data.agent);
      setSettings(data.settings);

      if (data.settings.welcome_message) {
        setMessages([{ role: 'assistant', content: data.settings.welcome_message }]);
      }
    } catch (err) {
      console.error(err);
      setError('error');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || sending) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setSending(true);

    try {
      const url = threadId
        ? `${BACKEND_URL}/api/public/agent/${publicKey}/conversation/${threadId}`
        : `${BACKEND_URL}/api/public/agent/${publicKey}/conversation`;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to send message');
      }

      const data = await res.json();
      if (data.thread_id && !threadId) setThreadId(data.thread_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.message || 'Sorry, I couldn\'t process that.' }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}`, isError: true }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
      <CircleNotch size={32} className="text-[#6366F1] animate-spin" />
    </div>
  );

  if (error === 'not_found') return <ErrorState icon={WarningCircle} title="Agent Not Found" description="This agent deployment doesn't exist or has been removed." />;
  if (error === 'revoked') return <ErrorState icon={ShieldSlash} title="Deployment Revoked" description="This agent deployment has been permanently revoked by its owner." />;
  if (error === 'paused') return <ErrorState icon={PauseCircle} title="Agent Paused" description="This agent is temporarily paused. Please check back later." />;
  if (error === 'domain_blocked') return <ErrorState icon={ShieldSlash} title="Domain Not Authorized" description="This agent is not authorized to run on this domain." />;
  if (error) return <ErrorState icon={WarningCircle} title="Something went wrong" description="We couldn't load this agent. Please try again later." />;

  const isDark = settings?.theme !== 'light';
  const bg = isDark ? '#161B26' : '#F8F9FA';
  const cardBg = isDark ? '#111214' : '#FFFFFF';
  const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const textPrimary = isDark ? '#FFFFFF' : '#111827';
  const textSecondary = isDark ? '#8b8fa3' : '#6B7280';
  const inputBg = isDark ? '#1A1B1E' : '#F3F4F6';

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: bg }} data-testid="public-agent-page">
      {/* Header */}
      <div className="shrink-0 px-5 py-4 border-b" style={{ borderColor, backgroundColor: cardBg }}>
        <div className="flex items-center gap-3 max-w-3xl mx-auto">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
            <Robot size={18} weight="duotone" className="text-white" />
          </div>
          <div>
            <h1 className="text-sm font-semibold" style={{ color: textPrimary }} data-testid="agent-title">
              {settings?.widget_title || agentInfo?.name}
            </h1>
            {agentInfo?.description && (
              <p className="text-xs" style={{ color: textSecondary }}>{agentInfo.description.substring(0, 80)}{agentInfo.description.length > 80 ? '...' : ''}</p>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-6" data-testid="messages-container">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.role}`}>
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white'
                    : msg.isError
                      ? 'border border-red-500/20 bg-red-500/5 text-red-400'
                      : ''
                }`}
                style={msg.role === 'assistant' && !msg.isError ? { backgroundColor: cardBg, border: `1px solid ${borderColor}`, color: textPrimary } : {}}
              >
                <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="rounded-2xl px-4 py-3" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-[#6366F1] animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-[#6366F1] animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 rounded-full bg-[#6366F1] animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="shrink-0 px-5 py-4 border-t" style={{ borderColor, backgroundColor: cardBg }}>
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 rounded-xl px-4 py-2" style={{ backgroundColor: inputBg }}>
            <textarea
              ref={inputRef}
              data-testid="message-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              rows={1}
              className="flex-1 bg-transparent border-none outline-none resize-none text-sm"
              style={{ color: textPrimary }}
              disabled={sending}
            />
            <button
              data-testid="send-message-btn"
              onClick={sendMessage}
              disabled={!input.trim() || sending}
              className="p-2 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white disabled:opacity-40 transition-opacity hover:opacity-90"
            >
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </div>
        </div>

        {settings?.show_branding && (
          <div className="text-center mt-3">
            <a
              href="https://manandmachine.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] hover:opacity-80 transition-opacity"
              style={{ color: textSecondary }}
              data-testid="branding-link"
            >
              Powered by Man & Machine
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
